import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Paper, Typography, Button, Box, Alert, CircularProgress,
  TextField, Stepper, Step, StepLabel, Grid, Card, CardContent
} from '@mui/material';
import { Security, Timer, Assessment } from '@mui/icons-material';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5006/api';

const ExamPage = () => {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({ email: '', fullName: '', registrationNumber: '', institution: '' });
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);

  useEffect(() => {
    if (shareCode) {
      axios.get(`${API_URL}/public/exam/${shareCode}`)
        .then(res => {
          setExam(res.data);
          setLoading(false);
          // Check if exam is scheduled for future
          if (res.data.scheduledAt) {
            const scheduled = new Date(res.data.scheduledAt);
            if (scheduled > new Date()) {
              setIsAvailable(false);
            }
          }
        })
        .catch(err => { setError('Exam not found'); setLoading(false); });
    }
  }, [shareCode]);

  const handleSendOTP = async () => {
    if (!formData.email) { toast.error('Enter your email'); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/auth/send-otp-exam`, { email: formData.email });
      setOtpSent(true);
      toast.success('Verification code sent!');
    } catch (err) { toast.error('Failed to send code'); }
    finally { setIsLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode) { toast.error('Enter the code'); return; }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/verify-otp`, { email: formData.email, otpCode });
      if (res.data.verified) setEmailVerified(true);
    } catch (err) { toast.error('Invalid code'); }
    finally { setIsLoading(false); }
  };

  const handleStartExam = async () => {
    if (!formData.fullName || !formData.registrationNumber) { toast.error('Fill all required fields'); return; }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API_URL}/test/start`, {
        email: formData.email,
        fullName: formData.fullName,
        registrationNumber: formData.registrationNumber,
        institution: formData.institution,
        examId: exam.id,
      });
      navigate('/test', { state: { testData: res.data } });
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to start'); }
    finally { setIsLoading(false); }
  };

  if (loading) return <Container sx={{ mt: 8, textAlign: 'center' }}><CircularProgress /></Container>;
  if (error) return <Container sx={{ mt: 8 }}><Alert severity="error">{error}</Alert></Container>;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Security sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>{exam.title}</Typography>
          {exam.description && <Typography variant="body1" color="text.secondary">{exam.description}</Typography>}
        </Box>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6}><Card><CardContent sx={{ textAlign: 'center' }}>
            <Timer color="primary" /><Typography variant="h6">{Math.floor(exam.timeLimit / 60)} min</Typography>
            <Typography variant="body2">Time Limit</Typography>
          </CardContent></Card></Grid>
          <Grid item xs={6}><Card><CardContent sx={{ textAlign: 'center' }}>
            <Assessment color="primary" /><Typography variant="h6">{exam.questionCount}</Typography>
            <Typography variant="body2">Questions</Typography>
          </CardContent></Card></Grid>
        </Grid>

        {exam.scheduledAt && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This exam is scheduled to start on <strong>{new Date(exam.scheduledAt).toLocaleString()}</strong>
          </Alert>
        )}

        {!isAvailable ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This exam is not yet available. It is scheduled for <strong>{new Date(exam.scheduledAt).toLocaleString()}</strong>. Please come back at the scheduled time.
          </Alert>
        ) : (
          <>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {['Register', 'Verify Email', 'Start'].map(l => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
        </Stepper>

        {activeStep === 0 && (
          <Box>
            <TextField fullWidth label="Full Name" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} margin="normal" required />
            <TextField fullWidth label="Email" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} margin="normal" required />
            <TextField fullWidth label="Registration Number" value={formData.registrationNumber} onChange={e => setFormData({...formData, registrationNumber: e.target.value})} margin="normal" required />
            <TextField fullWidth label="Institution (optional)" value={formData.institution} onChange={e => setFormData({...formData, institution: e.target.value})} margin="normal" />
            <Box sx={{ mt: 3, textAlign: 'right' }}><Button variant="contained" onClick={() => setActiveStep(1)}>Next</Button></Box>
          </Box>
        )}

        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center' }}>
            {!otpSent ? (
              <Button variant="contained" size="large" onClick={handleSendOTP} disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} /> : 'Send Verification Code'}
              </Button>
            ) : !emailVerified ? (
              <>
                <Typography sx={{ mb: 2 }}>Enter the 6-digit code sent to {formData.email}</Typography>
                <TextField fullWidth sx={{ maxWidth: 300, mx: 'auto', mb: 2 }}
                  value={otpCode} onChange={e => setOtpCode(e.target.value)}
                  inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '8px' } }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Button variant="contained" onClick={handleVerifyOTP} disabled={isLoading || otpCode.length < 6}>
                    {isLoading ? <CircularProgress size={24} /> : 'Verify'}
                  </Button>
                  <Button onClick={handleSendOTP} disabled={isLoading}>Resend</Button>
                </Box>
              </>
            ) : (
              <Alert severity="success">✅ Verified! You can now start the exam.</Alert>
            )}
            <Box sx={{ mt: 2, textAlign: 'left' }}><Button onClick={() => setActiveStep(0)}>Back</Button></Box>
          </Box>
        )}

        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 3 }}>Verified as <strong>{formData.fullName}</strong> ({formData.registrationNumber})</Alert>
            <Typography variant="h6" gutterBottom>Ready to begin?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {Math.floor(exam.timeLimit / 60)} minutes • {exam.questionCount} questions • Auto-submit on timeout
            </Typography>
            <Button variant="contained" size="large" color="success" onClick={handleStartExam} disabled={isLoading} sx={{ px: 6, py: 1.5 }}>
              {isLoading ? 'Starting...' : 'Accept and Start'}
            </Button>
          </Box>
        )}
        </>
      )}
      </Paper>
    </Container>
  );
};

export default ExamPage;
